import { useQuery } from '@tanstack/react-query';
import type { CardSearchResult } from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { searchRepository } from '@web/data/repositories';
import { useOrgContext } from '@web/ui/core';

import { presetToRange, type CardSearchFilters } from './card_filters';

const MIN_CHARS = 2;

export interface SearchState {
  results: readonly CardSearchResult[];
  isFetching: boolean;
  isActive: boolean;
}

/**
 * Org-scoped card search. Runs whenever the palette is open: with text (>= 2
 * chars) it ranks by relevance + recency; with none it browses newest-first.
 * Channel/date filters refine either mode.
 */
export function useSearch(
  debounced: string,
  filters: CardSearchFilters,
  open: boolean,
): SearchState {
  const { orgId } = useOrgContext();

  const hasText = debounced.length >= MIN_CHARS;
  const enabled = open;
  const { createdFrom } = presetToRange(filters.period);

  const query = useQuery({
    queryKey: queryKeys.search.query(
      orgId,
      hasText ? debounced : '',
      filters.channels,
      filters.period,
    ),
    queryFn: () =>
      searchRepository.search(orgId, {
        q: hasText ? debounced : undefined,
        channels: filters.channels,
        createdFrom,
      }),
    enabled,
  });

  return {
    results: query.data ?? [],
    isFetching: enabled && query.isFetching,
    // isActive drives the status line: true whenever the card query is live.
    isActive: enabled,
  };
}
